/**
 * Fantasy Console 0 - Keyboard Shortcuts Hook
 */

import { useEffect, useCallback } from 'react'
import { TabId } from '../types'

interface ShortcutHandlers {
  onSwitchTab: (tab: TabId) => void
  onSave?: () => void
  onNewCart?: () => void
  onImportCart?: () => void
  onExportCart?: () => void
  onRunCart?: () => void
  onPauseResume?: () => void
  onShowShortcuts?: () => void
}

export function useKeyboardShortcuts(handlers: ShortcutHandlers) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return

      if (e.key === '1') {
        e.preventDefault()
        handlers.onSwitchTab('code')
      } else if (e.key === '2') {
        e.preventDefault()
        handlers.onSwitchTab('sprites')
      } else if (e.key === '3') {
        e.preventDefault()
        handlers.onSwitchTab('map')
      } else if (e.key === '4') {
        e.preventDefault()
        handlers.onSwitchTab('sound')
      } else if (e.key === '5') {
        e.preventDefault()
        handlers.onSwitchTab('library')
      } else if (e.key === '6') {
        e.preventDefault()
        handlers.onSwitchTab('play')
      } else if (e.key === 's') {
        e.preventDefault()
        handlers.onSave?.()
      } else if (e.key === 'n') {
        e.preventDefault()
        handlers.onNewCart?.()
      } else if (e.key === 'o') {
        e.preventDefault()
        handlers.onImportCart?.()
      } else if (e.key === 'e') {
        e.preventDefault()
        handlers.onExportCart?.()
      } else if (e.key === 'r') {
        e.preventDefault()
        handlers.onRunCart?.()
      } else if (e.key === '.') {
        e.preventDefault()
        handlers.onPauseResume?.()
      } else if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault()
        handlers.onShowShortcuts?.()
      }
    },
    [handlers],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}

export interface ShortcutEntry {
  keys: string[]
  action: string
}

export interface ShortcutSection {
  title: string
  shortcuts: ShortcutEntry[]
}

export const SHORTCUT_SECTIONS: ShortcutSection[] = [
  {
    title: 'General',
    shortcuts: [
      { keys: ['Ctrl/Cmd', '1'], action: 'Switch to Code tab' },
      { keys: ['Ctrl/Cmd', '2'], action: 'Switch to Sprites tab' },
      { keys: ['Ctrl/Cmd', '3'], action: 'Switch to Map tab' },
      { keys: ['Ctrl/Cmd', '4'], action: 'Switch to Sound tab' },
      { keys: ['Ctrl/Cmd', '5'], action: 'Switch to Library view' },
      { keys: ['Ctrl/Cmd', '6'], action: 'Switch to Play view' },
      { keys: ['Ctrl/Cmd', 'S'], action: 'Save cart to cloud' },
      { keys: ['Ctrl/Cmd', 'N'], action: 'New cart' },
      { keys: ['Ctrl/Cmd', 'O'], action: 'Import cart' },
      { keys: ['Ctrl/Cmd', 'E'], action: 'Export cart' },
      { keys: ['Ctrl/Cmd', 'R'], action: 'Reload cart' },
      { keys: ['Ctrl/Cmd', '.'], action: 'Pause/Resume console' },
      { keys: ['Ctrl/Cmd', '?'], action: 'Show keyboard shortcuts' },
    ],
  },
  {
    title: 'Code Editor',
    shortcuts: [
      { keys: ['Ctrl/Cmd', 'Enter'], action: 'Run cart' },
      { keys: ['Ctrl/Cmd', 'F'], action: 'Find' },
      { keys: ['Ctrl/Cmd', 'H'], action: 'Find and replace' },
      { keys: ['Ctrl/Cmd', '/'], action: 'Toggle comment' },
      { keys: ['Ctrl/Cmd', 'Z'], action: 'Undo' },
      { keys: ['Ctrl/Cmd', 'Shift', 'Z'], action: 'Redo' },
    ],
  },
  {
    title: 'Sprite Editor',
    shortcuts: [
      { keys: ['P'], action: 'Pencil tool' },
      { keys: ['F'], action: 'Fill tool' },
      { keys: ['L'], action: 'Line tool' },
      { keys: ['R'], action: 'Rectangle tool' },
      { keys: ['E'], action: 'Eraser tool' },
      { keys: ['I'], action: 'Color picker' },
      { keys: ['0-9'], action: 'Select color 0-9' },
      { keys: ['Shift', '0-5'], action: 'Select color 10-15' },
      { keys: ['[/]'], action: 'Previous/Next sprite' },
      { keys: ['Arrow Keys'], action: 'Navigate sprite grid' },
      { keys: ['Ctrl/Cmd', 'Z'], action: 'Undo' },
      { keys: ['Ctrl/Cmd', 'Shift', 'Z'], action: 'Redo' },
      { keys: ['Ctrl/Cmd', 'C'], action: 'Copy sprite' },
      { keys: ['Ctrl/Cmd', 'V'], action: 'Paste sprite' },
    ],
  },
  {
    title: 'Map Editor',
    shortcuts: [
      { keys: ['P'], action: 'Pencil tool' },
      { keys: ['F'], action: 'Fill tool' },
      { keys: ['R'], action: 'Rectangle tool' },
      { keys: ['E'], action: 'Eraser tool' },
      { keys: ['I'], action: 'Tile picker' },
      { keys: ['[/]'], action: 'Previous/Next tile' },
      { keys: ['+/-'], action: 'Zoom in/out' },
      { keys: ['G'], action: 'Toggle grid' },
      { keys: ['Arrow Keys'], action: 'Pan camera' },
      { keys: ['Ctrl/Cmd', 'Z'], action: 'Undo' },
      { keys: ['Ctrl/Cmd', 'Shift', 'Z'], action: 'Redo' },
    ],
  },
  {
    title: 'Sound Editor',
    shortcuts: [
      { keys: ['Space'], action: 'Play/Stop' },
      { keys: ['[/]'], action: 'Previous/Next SFX' },
      { keys: ['1-8'], action: 'Select waveform' },
      { keys: ['Ctrl/Cmd', 'A'], action: 'Select all' },
      { keys: ['Del/Backspace'], action: 'Delete selected' },
      { keys: ['Escape'], action: 'Clear selection' },
      { keys: ['Ctrl/Cmd', 'Z'], action: 'Undo' },
      { keys: ['Ctrl/Cmd', 'Shift', 'Z'], action: 'Redo' },
      { keys: ['Ctrl/Cmd', 'C'], action: 'Copy SFX' },
      { keys: ['Ctrl/Cmd', 'V'], action: 'Paste SFX' },
      { keys: ['\u2191/\u2193'], action: 'Transpose selection by 1' },
      { keys: ['Shift', '\u2191/\u2193'], action: 'Transpose selection by 12' },
    ],
  },
]
